/**
 * Per-site detail: one row per monitored site, grouped by business unit.
 *
 * Altitude note (dashboard design rules §1): this table stays on the L1 board
 * deliberately — the estate is a dozen-odd endpoints and the heat rows above
 * already render one row per site, so hiding the same cardinality behind a
 * drill would add a click and remove nothing. Each row is a door: the site
 * name opens the site's L2 drawer (`?site=<id>`, URL-held).
 *
 * Every latency figure in this table (latest, average, p50/p95, the row line)
 * comes from the backend's in-memory ring — the LIVE window of roughly the last
 * hour. The card header says so, because an unqualified p95 reads as all-time,
 * and the durable rollups behind the heat rows above keep means only.
 */

import { Chart } from "@norican/dataviz";
import { ChevronRight, ExternalLink, Icon } from "@norican/icons";
import { Badge, Button, Card, DataGrid, Typography } from "@norican/react";

import type { SiteStatus } from "@/lib/api";
import { useDrawerParam } from "@/lib/drill";

import {
  displayHost,
  driftTitle,
  formatMs,
  formatPct,
  redirectDrift,
  SITE_DETAIL_PARAM,
  STATUS_COLOR,
  STATUS_LABEL,
  STATUS_TONE,
} from "./status";

interface SiteTableProps {
  sites: SiteStatus[];
}

export function SiteTable({ sites }: SiteTableProps) {
  const drawer = useDrawerParam(SITE_DETAIL_PARAM);
  // Sites join the ring at different times, so the window is "up to" this many.
  const windowChecks = Math.max(0, ...sites.map((site) => site.samples ?? 0));

  return (
    <Card variant="outline">
      <Card.Header>
        <div className="flex items-baseline justify-between gap-4">
          <Typography variant="heading-4" as="h2">
            Monitored sites
          </Typography>
          <Typography variant="body-sm" tone="muted">
            {sites.length} endpoints
          </Typography>
        </div>
        {/* Coverage beside the numbers (rules §5) — which window they describe. */}
        <Typography variant="caption" as="p" className="mt-1">
          Response, average and p50/p95 cover the live window: up to the last {windowChecks || "—"}{" "}
          checks per site (~1 hour). The daily history above keeps means only, so no percentile
          exists for it.
        </Typography>
      </Card.Header>
      <Card.Body padding="none">
        <DataGrid<SiteStatus>
          data={sites}
          getRowId={(site) => site.id}
          density="comfortable"
          columns={[
            {
              id: "name",
              header: "Site",
              accessor: (site) => site.name,
              sortable: true,
              cell: (site) => <SiteCell site={site} onOpen={() => drawer.open(site.id)} />,
            },
            {
              id: "business",
              header: "Business",
              accessor: (site) => site.business,
              sortable: true,
              cell: (site) => <span className="text-fg-muted text-sm">{site.business}</span>,
            },
            {
              id: "status",
              header: "Status",
              accessor: (site) => site.status,
              sortable: true,
              cell: (site) => (
                <Badge tone={STATUS_TONE[site.status]} variant="subtle" size="sm">
                  {STATUS_LABEL[site.status]}
                </Badge>
              ),
            },
            {
              id: "response",
              header: "Response",
              align: "end",
              accessor: (site) => site.response_time_ms ?? null,
              sortable: true,
              cell: (site) => (
                <div className="flex flex-col items-end">
                  <span className="text-fg tabular-nums">{formatMs(site.response_time_ms)}</span>
                  {/* The mean the backend already computes, finally rendered. */}
                  <span className="text-fg-muted text-xs tabular-nums">
                    avg {formatMs(site.avg_response_time_ms)}
                  </span>
                </div>
              ),
            },
            {
              id: "percentiles",
              header: "p50 / p95 (live)",
              align: "end",
              accessor: (site) => site.p95_response_time_ms ?? null,
              sortable: true,
              cell: (site) => (
                <span className="text-fg-muted text-sm tabular-nums">
                  {formatMs(site.p50_response_time_ms)} / {formatMs(site.p95_response_time_ms)}
                </span>
              ),
            },
            {
              id: "trend",
              header: "Trend",
              width: 130,
              cell: (site) => <LiveTrendCell site={site} />,
            },
            {
              id: "uptime",
              header: "Availability",
              align: "end",
              accessor: (site) => site.uptime_pct ?? null,
              sortable: true,
              cell: (site) => (
                <div className="flex flex-col items-end">
                  <span className="text-fg tabular-nums">{formatPct(site.uptime_pct)}</span>
                  <span className="text-fg-muted text-xs">
                    {site.samples ?? 0} {site.samples === 1 ? "check" : "checks"}
                  </span>
                </div>
              ),
            },
            {
              id: "detail",
              header: "Detail",
              accessor: (site) => site.last_error ?? site.status_code ?? null,
              cell: (site) =>
                site.last_error ? (
                  <span className="text-danger-fg text-xs">{site.last_error}</span>
                ) : (
                  <span className="text-fg-muted text-xs tabular-nums">
                    HTTP {site.status_code ?? "—"}
                  </span>
                ),
            },
            {
              id: "open",
              header: "",
              width: 96,
              align: "center",
              pinned: "right",
              cell: (site) => (
                <div className="flex items-center justify-center gap-1">
                  <a
                    href={site.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-fg-muted hover:text-accent inline-flex items-center justify-center"
                    aria-label={`Open ${site.name} in a new tab`}
                  >
                    <Icon icon={ExternalLink} size="sm" />
                  </a>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Details for ${site.name}`}
                    onClick={() => drawer.open(site.id)}
                  >
                    <Icon icon={ChevronRight} size="sm" />
                  </Button>
                </div>
              ),
            },
          ]}
        />
      </Card.Body>
    </Card>
  );
}

/** The site's name, its registered host, and where the probe actually landed. */
function SiteCell({ site, onOpen }: { site: SiteStatus; onOpen: () => void }) {
  const drift = redirectDrift(site);

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={onOpen}
        className="text-fg hover:text-accent text-left font-medium underline-offset-2 hover:underline"
        aria-label={`Open details for ${site.name}`}
      >
        {site.name}
      </button>
      <span className="text-fg-muted text-xs">{displayHost(site.url)}</span>
      {drift && (
        <span className="text-warning-fg text-xs" title={driftTitle(drift)}>
          {/* Keep the scheme when the hops changed it — http → https is the
              whole point of the warning, and stripping it hides that. */}
          → {drift.schemeChanged ? drift.finalUrl : displayHost(drift.finalUrl)} · {drift.hops}{" "}
          {drift.hops === 1 ? "redirect" : "redirects"}
        </span>
      )}
    </div>
  );
}

/**
 * One site's live response-time line, oldest check on the left.
 *
 * WORKAROUND (design-system gap): both DS sparklines (`MiniSparkline` and
 * `dataviz`'s `Sparkline`) take `number[] | {value:number}[]`, so neither can
 * express a check that produced no response — and substituting 0 ms is exactly
 * the inversion this cell used to show, an outage drawn as the site's fastest
 * ever reply. So this is `Sparkline`'s own recipe (`Chart.Container` size `sm`
 * + a bare `Chart.LineChart` + one `Chart.Line`, no axes) with the single
 * change that a `null` stays null: the kit then leaves the gap open, the same
 * behaviour `latency-trend.tsx` relies on for days with no checks. Still
 * DS-only — no hand-drawn SVG, no other chart library. Swap back to
 * `MiniSparkline` the moment its `data` accepts `(number | null)[]`.
 */
function LiveTrendCell({ site }: { site: SiteStatus }) {
  const trend = site.latency_trend ?? [];
  const measured = trend.filter((ms) => ms != null).length;

  if (trend.length < 2) {
    // One sample is not a trend — wait for the next cycle.
    return <span className="text-fg-subtle text-xs">Collecting…</span>;
  }

  const gaps = trend.length - measured;
  const summary =
    `${measured} of ${trend.length} recent checks answered` +
    (gaps > 0 ? ` · ${gaps} with no response (gaps in the line)` : "");

  return (
    <Chart.Container
      // Status colours are reserved for status (rules §3.2) — the line wears
      // the same tone as the row's badge, injected as `--color-latency`.
      config={{ latency: { label: "Response time", color: STATUS_COLOR[site.status] } }}
      size="sm"
      className="h-6 w-28"
      aria-label={`Live response time trend for ${site.name} — ${summary}`}
      title={summary}
    >
      <Chart.LineChart
        data={trend.map((ms, index) => ({ check: index + 1, latency: ms }))}
        margin={{ top: 3, right: 3, bottom: 3, left: 3 }}
      >
        <Chart.Line
          {...Chart.seriesDefaults}
          dataKey="latency"
          type="monotone"
          stroke="var(--color-latency)"
          strokeWidth={1.5}
          // A measured check between two gaps has no segment to draw, so it
          // needs its own mark or it would vanish from the row entirely.
          dot={{ r: 1, fill: "var(--color-latency)", stroke: "var(--color-latency)" }}
        />
      </Chart.LineChart>
    </Chart.Container>
  );
}
